import { collection, getDocs } from "firebase/firestore";
import { firestore } from "../config/firebase.config";
import facultyService from "./faculty.service";

export interface CareerRedirectUser {
  nombre: string;
  apellido: string;
  telefono: string;
  [key: string]: any;
}

export interface CareerRedirectEvent {
  id: string;
  fecha: string;
  timestamp: number;
  career_name: string;
  career_id: string;
  faculty_id: string;
  faculty_name?: string;
  institute_id: string;
  siteUrl: string;
  user_email: string;
  user_object: CareerRedirectUser;
}

export interface CareerRedirectSummary {
  id: string; // único para el DataGrid (ej: careerId o slug)
  careerId: string;
  facultyId: string;
  facultyName: string;
  careerName: string;
  redirectCount: number;
  events: CareerRedirectEvent[];
}

export interface RedirectsFilterOptions {
  days?: number;
  startDate?: string;
  endDate?: string;
  career_id?: string;
  forceRefresh?: boolean;
}

export interface CareerRedirectsResponse {
  summaries: CareerRedirectSummary[];
  allEvents: CareerRedirectEvent[];
}

const CACHE_KEY_PREFIX = "career_redirects_cache_";
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutos (600,000 ms)

class CareerRedirectsService {
  // URLs de endpoints: prioriza la Cloud Function local en desarrollo
  private localUrl = "http://127.0.0.1:5001/getCareerRedirects";
  private localFallbackUrl = "http://127.0.0.1:5001/getRedirectByInstitute";
  private primaryUrl = "https://us-central1-guia-universitaria-dev.cloudfunctions.net/getCareerRedirects";
  private fallbackUrl = "https://us-central1-guia-universitaria-dev.cloudfunctions.net/getRedirectByInstitute";

  /**
   * Obtiene los eventos de redirección para una institución.
   * Si se especifican fechas (startDate y endDate), la consulta es bajo demanda y NO utiliza caché.
   * Para períodos estándar (ej. 90 días), cuenta con un caché en sessionStorage válido por 10 minutos.
   */
  async getRedirects(
    uidInstitution: string | number,
    optionsOrDays: number | RedirectsFilterOptions = 90,
    forceRefreshParam: boolean = false
  ): Promise<CareerRedirectsResponse> {
    const instIdStr = String(uidInstitution);
    const instIdNum = parseInt(instIdStr, 10);

    const isOptionsObject = typeof optionsOrDays === "object" && optionsOrDays !== null;
    const days = isOptionsObject ? (optionsOrDays.days || 90) : (optionsOrDays || 90);
    const startDate = isOptionsObject ? optionsOrDays.startDate : undefined;
    const endDate = isOptionsObject ? optionsOrDays.endDate : undefined;
    const career_id = isOptionsObject ? optionsOrDays.career_id : undefined;
    const forceRefresh = isOptionsObject
      ? (optionsOrDays.forceRefresh ?? false)
      : forceRefreshParam;

    const isDateFilter = Boolean(startDate || endDate);
    const cacheKey = `${CACHE_KEY_PREFIX}${instIdStr}_${days}`;

    // Verificar si existe información en caché no expirada (SÓLO si NO es consulta por filtro de fechas)
    if (!forceRefresh && !isDateFilter) {
      try {
        const cachedItem = sessionStorage.getItem(cacheKey);
        if (cachedItem) {
          const { timestamp, data } = JSON.parse(cachedItem);
          if (Date.now() - timestamp < CACHE_TTL_MS) {
            console.log("Cargando redirecciones desde caché local (válido por 10 min)");
            return data as CareerRedirectsResponse;
          }
        }
      } catch (e) {
        console.warn("Error leyendo caché de redirecciones:", e);
      }
    }

    const bodyPayload: Record<string, any> = {
      uidInstitution: instIdStr,
    };
    if (isDateFilter) {
      if (startDate) bodyPayload.startDate = startDate;
      if (endDate) bodyPayload.endDate = endDate;
    } else {
      bodyPayload.days = days;
    }
    if (career_id) {
      bodyPayload.career_id = career_id;
    }

    let rawEvents: any[] = [];
    let isPrimarySuccess = false;

    // 1. Intentar con la función local en 127.0.0.1:5001 (Google Analytics BigQuery)
    try {
      const response = await fetch(this.localUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bodyPayload),
      });

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          console.log(`[careerRedirects] Obtenidos ${data.length} eventos desde Cloud Function LOCAL (puerto 5001).`);
          rawEvents = data;
          isPrimarySuccess = true;
        }
      }
    } catch (localErr) {
      console.warn("[careerRedirects] Servidor local no disponible en puerto 5001, intentando cloud:", localErr);
    }

    // 2. Si no respondió localmente, intentar con la función en la nube
    if (!isPrimarySuccess) {
      try {
        const response = await fetch(this.primaryUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bodyPayload),
        });

        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            rawEvents = data;
            isPrimarySuccess = true;
          }
        }
      } catch (err) {
        console.warn("getCareerRedirects cloud endpoint no disponible, utilizando fallback:", err);
      }
    }

    // 3. Fallback a getRedirectByInstitute (local primero, luego nube)
    if (!isPrimarySuccess) {
      try {
        let response = await fetch(this.localFallbackUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uidInstitution: instIdNum }),
        }).catch(() => null);

        if (!response || !response.ok) {
          response = await fetch(this.fallbackUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ uidInstitution: instIdNum }),
          });
        }

        if (response && response.ok) {
          const data = await response.json();
          rawEvents = Array.isArray(data) ? data.flat() : [];
        }
      } catch (fallbackErr) {
        console.error("Error al consultar redirecciones desde el fallback:", fallbackErr);
      }
    }

    // 3. Obtener facultades de Firestore para mapear IDs a nombres
    const facultiesMap: Record<string, string> = {};
    try {
      const faculties = await facultyService.getAll(instIdStr);
      if (faculties && faculties.length > 0) {
        faculties.forEach((f) => {
          if (f.id) {
            facultiesMap[f.id] = f.name;
          }
        });
      }
    } catch (facErr) {
      console.warn("No se pudieron cargar facultades de Firestore:", facErr);
    }

    // 4. Normalizar y parsear eventos individuales
    const parsedEvents: CareerRedirectEvent[] = [];

    rawEvents.forEach((item: any, idx: number) => {
      // Si vino estructurado desde getCareerRedirects
      if (isPrimarySuccess && item.career_name && item.user_object) {
        const facultyName = facultiesMap[item.faculty_id] || item.faculty_name || "Facultad no especificada";
        parsedEvents.push({
          id: item.id || `ev-${item.timestamp || Date.now()}-${idx}`,
          fecha: item.fecha || (item.timestamp ? new Date(item.timestamp).toISOString() : new Date().toISOString()),
          timestamp: item.timestamp || Date.now(),
          career_name: item.career_name,
          career_id: item.career_id || "",
          faculty_id: item.faculty_id || "",
          faculty_name: facultyName,
          institute_id: item.institute_id || instIdStr,
          siteUrl: item.siteUrl || "",
          user_email: item.user_email || "ANON",
          user_object: {
            nombre: item.user_object?.nombre || "",
            apellido: item.user_object?.apellido || "",
            telefono: item.user_object?.telefono || "",
            ...item.user_object,
          },
        });
        return;
      }

      // Si vino desde el endpoint de respaldo getRedirectByInstitute
      const params = item.event_params || [];
      const paramsMap: Record<string, any> = {};
      for (const p of params) {
        paramsMap[p.key] =
          p.value?.string_value ??
          p.value?.int_value ??
          p.value?.float_value ??
          p.value?.double_value;
      }

      // Validar que pertenezca a la institución
      const eventInst = String(paramsMap.institute_id || paramsMap.institution || "");
      if (eventInst && eventInst !== instIdStr) {
        return;
      }

      // Extraer datos de la carrera
      const careerName = paramsMap.career_name || paramsMap.name || "Carrera no especificada";
      let careerId = String(paramsMap.career_id || paramsMap.code || "");
      const careerObjRaw = paramsMap.career_object;
      if (!careerId && careerObjRaw && typeof careerObjRaw === "string") {
        const codeMatch = careerObjRaw.match(/"code"\s*:\s*"([^"]+)"/) || careerObjRaw.match(/"code"\s*:\s*(\d+)/);
        if (codeMatch) careerId = codeMatch[1];
        const idMatch = careerObjRaw.match(/"id"\s*:\s*"([^"]+)"/);
        if (!careerId && idMatch) careerId = idMatch[1];
      }

      const facultyId = String(paramsMap.faculty_id || "");
      const facultyName = facultiesMap[facultyId] || "Facultad no especificada";

      // Parsear user_object
      let userParsed: Record<string, any> = {};
      if (paramsMap.user_object && typeof paramsMap.user_object === "string") {
        try {
          let cleanStr = paramsMap.user_object;
          if (cleanStr.startsWith('"') && cleanStr.endsWith('"')) {
            cleanStr = JSON.parse(cleanStr);
          }
          userParsed = typeof cleanStr === "string" ? JSON.parse(cleanStr) : cleanStr;
        } catch (e) {
          userParsed = {};
        }
      }

      // Determinar timestamp / fecha
      let timestamp = Date.now();
      if (paramsMap.batch_page_id && Number(paramsMap.batch_page_id) > 1000000000) {
        timestamp = Number(paramsMap.batch_page_id);
      } else if (paramsMap.ga_session_id && Number(paramsMap.ga_session_id) > 1000000) {
        timestamp = Number(paramsMap.ga_session_id) * 1000;
      }

      const eventDate = new Date(timestamp);

      parsedEvents.push({
        id: `ev-${timestamp}-${idx}`,
        fecha: eventDate.toISOString(),
        timestamp,
        career_name: careerName,
        career_id: careerId,
        faculty_id: facultyId,
        faculty_name: facultyName,
        institute_id: instIdStr,
        siteUrl: paramsMap.siteUrl || paramsMap.webSite || "",
        user_email: paramsMap.user_email || "ANON",
        user_object: {
          nombre: userParsed.name || userParsed.nombre || userParsed.firstName || "",
          apellido: userParsed.lastName || userParsed.apellido || userParsed.lastname || "",
          telefono: userParsed.phone || userParsed.telefono || userParsed.phoneNumber || "",
          ...userParsed,
        },
      });
    });

    // 5. Ordenar todos los eventos en orden descendente por fecha
    parsedEvents.sort((a, b) => b.timestamp - a.timestamp);

    // Guardar en sessionStorage para compatibilidad con TemplateReport
    try {
      sessionStorage.setItem("redirectsByInstitute", JSON.stringify(rawEvents));
      sessionStorage.setItem("careerRedirectsParsed", JSON.stringify(parsedEvents));
    } catch (e) {
      console.warn("No se pudo almacenar redirectsByInstitute en sessionStorage", e);
    }

    // 6. Agrupar por carrera
    const groups: Record<string, { careerName: string; facultyId: string; careerId: string; events: CareerRedirectEvent[] }> = {};

    for (const ev of parsedEvents) {
      // Clave de agrupación: combinación de facultad y nombre de carrera normalizado
      const groupKey = `${ev.faculty_id || "sin-facultad"}___${ev.career_name.trim().toLowerCase()}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          careerName: ev.career_name.trim(),
          facultyId: ev.faculty_id,
          careerId: ev.career_id,
          events: [],
        };
      }

      if (!groups[groupKey].careerId && ev.career_id) {
        groups[groupKey].careerId = ev.career_id;
      }

      groups[groupKey].events.push(ev);
    }

    // 7. Si alguna carrera no tiene ID de carrera aún, intentar buscarlo en Firestore
    const summaryList: CareerRedirectSummary[] = [];

    for (const [key, group] of Object.entries(groups)) {
      let finalCareerId = group.careerId;

      if (!finalCareerId && group.facultyId && instIdStr) {
        try {
          const careersCol = collection(firestore, `institutes/${instIdStr}/faculties/${group.facultyId}/careers`);
          const snapshot = await getDocs(careersCol);
          const found = snapshot.docs.find((doc) => {
            const data = doc.data();
            return (
              data.name &&
              data.name.trim().toLowerCase() === group.careerName.toLowerCase()
            );
          });

          if (found) {
            const foundData = found.data();
            finalCareerId = foundData.code || found.id || "";
          }
        } catch (findErr) {
          console.warn(`No se pudo resolver ID para la carrera ${group.careerName}:`, findErr);
        }
      }

      // Si aún no tiene ID, asignar "N/A" o valor por defecto
      if (!finalCareerId) {
        finalCareerId = "N/A";
      }

      // Asignar el ID resuelto a todos los eventos del grupo
      group.events.forEach((ev) => {
        if (!ev.career_id || ev.career_id === "N/A") {
          ev.career_id = finalCareerId;
        }
      });

      const facultyName = facultiesMap[group.facultyId] || "Facultad no especificada";

      summaryList.push({
        id: key,
        careerId: finalCareerId,
        facultyId: group.facultyId,
        facultyName,
        careerName: group.careerName,
        redirectCount: group.events.length,
        events: group.events,
      });
    }

    // 8. Ordenar los resúmenes en orden descendente por Cantidad de Redirecciones
    summaryList.sort((a, b) => b.redirectCount - a.redirectCount);

    const resultResponse: CareerRedirectsResponse = {
      summaries: summaryList,
      allEvents: parsedEvents,
    };

    // Almacenar en caché para evitar consultas repetitivas (10 minutos)
    // SÓLO si no es una consulta personalizada con fechas bajo demanda
    if (!isDateFilter) {
      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({ timestamp: Date.now(), data: resultResponse })
        );
      } catch (cacheSaveErr) {
        console.warn("No se pudo almacenar el resultado en caché:", cacheSaveErr);
      }
    }

    return resultResponse;
  }

  /**
   * Limpia el caché en memoria / sessionStorage para forzar una consulta fresca
   */
  clearCache(uidInstitution?: string | number): void {
    try {
      if (uidInstitution) {
        const prefix = `${CACHE_KEY_PREFIX}${uidInstitution}`;
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith(prefix)) {
            sessionStorage.removeItem(key);
          }
        }
      } else {
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith(CACHE_KEY_PREFIX)) {
            sessionStorage.removeItem(key);
          }
        }
      }
    } catch (e) {
      console.warn("Error al limpiar caché:", e);
    }
  }
}

export default new CareerRedirectsService();

